import 'normalize.css';
import React, { Fragment } from 'react';
import { Router, Route, Redirect, browserHistory, IndexRoute } from 'react-router';
import { render } from 'react-dom';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'mobx-react';
import '../reset.css';
import CustomizeUIPage from '../Components/ConsoleCustomizeUI/CustomizeUIPage';
import PolicyMigrationPage from '../Components/PolicyMigration/PolicyMigrationPage';
import { Raven } from '../debug';
import RootPage from '../RootPage';
import i18n from '../browser/i18n';
import ConsoleLogin from '../Components/Authentication/ConsoleLogin';
import ForgotPasswordPage from '../Components/Authentication/ForgotPasswordPage';
import SetupPasswordPage from '../Components/Authentication/SetupPasswordPage';
import LoginFailureSso from '../Components/Authentication/LoginFailureSSO';
import initConsoleStores from './initConsoleStores';
import ConsoleLayout from '../Components/Console/ConsoleLayout';
import Dashboard from '../Components/Dashboard/Dashboard';
import URLIsoDashboard from '../Components/Dashboard/URLIsoDashboard';
import BrowserPolicies from '../Components/BrowserPolicies/BrowserPolicies';
import ConsoleFeatures from '../Components/ConsoleFeatures/ConsoleFeatures';
import ConsoleUDPolicies from '../Components/ConsoleUDPolicies/ConsoleUDPolicies';
import RegistrationNotice from '../Components/RegistrationNotice/RegistrationNotice';
import ConsolePolicyAPI from '../Components/ConsolePolicyAPI/ConsolePolicyAPI';
import ConsoleReportingAPI from '../Components/ConsoleReportingAPI/ConsoleReportingAPI';
import ConsoleCustomPages from '../Components/ConsoleCustomPages/ConsoleCustomPages';
import ConsoleAudit from '../Components/ConsoleAudit/ConsoleAudit';
import AuthenticationConfigurationPage from '../Components/AuthenticationConfiguration/AuthenticationConfigurationPage';
import initData from './consoleInitData';
import GAController from './GAController';
import initPendo from './initPendo';
import LocalAccounts from '../Components/LocalAccounts/LocalAccounts';
import ConsoleSamlAccess from '../Components/ConsoleSamlAccess/ConsoleSamlAccess';
import RegistrationOptions from '../Components/RegistrationOptions/RegistrationOptions';
import PoliciesOverview from '../Components/Policy/Overview/Overview';
import BrowsingRoles from '../Components/Policy/BrowsingRoles/BrowsingRoles';
import RedirectRules from '../Components/Policy/RedirectRules/RedirectRules';
import ExclusionRules from '../Components/Policy/ExclusionRules/ExclusionRules';
import ContentRules from '../Components/Policy/ContentRules/ContentRules';
import CustomCategories from '../Components/Policy/CustomCategories/CustomCategories';
import AccessCodes from '../Components/Policy/AccessCodes/AccessCodes';
import BrowserBarOptionsPage from '../Components/BrowserBarOptions/BrowserBarOptionsPage';
import GeneralOptions from '../Components/RegistrationOptions/GeneralOptions';
import PolicyTesting from '../Components/Policy/PolicyTesting/PolicyTesting';
import ConsoleExitMessage from '../Components/ConsoleExitMessage/ConsoleExitMessage';
import FileThreatRules from '../Components/Policy/FileRules/FileThreatRules/FileThreatRules';
import DlpRules from '../Components/Policy/FileRules/DlpRules/DlpRules';

const stores = initConsoleStores(initData.stores, true);
initPendo(initData, stores);

Raven.setEnvironment(initData.env);
Raven.setTagsContext({
    bundle: 'console',
    mirrorRelease: initData.release,
    customerId: stores.console.customerId,
    realCustomerId: stores.console.realCustomerId,
});

const { useRedesignedPolicy } = stores.console;
const canAccessUrlIso = stores.console.hasUrlIsolation() || stores.console.isConsoleAdmin;
const canAccessBrowserIso = stores.console.canAccessBrowserIso() || stores.console.isConsoleAdmin;
const customizeUIEnabled = stores.console.isMirrorFeatureEnabled('CUSTOMIZE_UI');

const getDefaultRoute = () => {
    if (canAccessBrowserIso) {
        return 'dashboards/browser_isolation';
    }
    if (canAccessUrlIso) {
        return 'dashboards/url_isolation';
    }
    if (useRedesignedPolicy) {
        return 'policies/overview';
    }
    return 'policies';
};

render(<I18nextProvider i18n={i18n}>
    <Provider {...stores}>
        <GAController>
            <RootPage appName="console">
                <Router history={browserHistory}>
                    <Route path="/console">
                        <IndexRoute component={ConsoleLogin} />
                        <Route path="forgot" component={ForgotPasswordPage} />
                        <Route path="forgot/:token" component={SetupPasswordPage} />
                        <Route path="sso/error" component={LoginFailureSso} />
                        <Route component={ConsoleLayout}>
                            {canAccessUrlIso ?
                                <Route path="dashboards/url_isolation" component={URLIsoDashboard} /> : null}
                            {canAccessBrowserIso ?
                                <Route path="dashboards/browser_isolation" component={Dashboard} /> : null}
                            <Route path="product_settings/policy_api" component={ConsolePolicyAPI} />
                            <Route path="product_settings/reporting_api" component={ConsoleReportingAPI} />
                            {/* The menu hides or shows a link to this path conditionally, we expose it always so */}
                            {/* we can test the route just by flipping the flag ourselves through the dev console */}
                            <Route path="product_settings/new_policies_migration" component={PolicyMigrationPage} />
                            <Route
                                path="product_settings/saml_sso_configuration"
                                component={AuthenticationConfigurationPage}
                            />
                            <Route path="administrators/local_accounts" component={LocalAccounts} />
                            <Route path="administrators/saml_sso_access" component={ConsoleSamlAccess} />
                            {customizeUIEnabled ? <>
                                <Route path="customize_ui" component={CustomizeUIPage} />
                                <Redirect from="customize_ui/*" to="customize_ui" />
                            </> : <>
                                {stores.console.canCustomizeBrowserBar() ? <Fragment>
                                    <Route path="customize_ui/browser_bar" component={BrowserBarOptionsPage} />
                                </Fragment> : null}
                                <Route path="customize_ui/block_pages" component={ConsoleCustomPages} />
                                <Route path="customize_ui/registration_notice" component={RegistrationNotice} />
                                <Route path="customize_ui/exit_message" component={ConsoleExitMessage} />
                            </>}
                            <Route path="feature_board" component={ConsoleFeatures} />
                            <Route path="audit_log" component={ConsoleAudit} />
                            <Route path="policies/overview" component={PoliciesOverview} />
                            <Route path="policies/browsing_roles" component={BrowsingRoles} />
                            <Route path="policies/redirect_rules" component={RedirectRules} />
                            <Route path="policies/exclusion_rules" component={ExclusionRules} />
                            <Route path="policies/file_threat_rules" component={FileThreatRules} />
                            <Route path="policies/dlp_rules" component={DlpRules} />
                            <Route path="policies/content_rules" component={ContentRules} />
                            <Route path="policies/custom_categories" component={CustomCategories} />
                            <Route path="policies/access_codes" component={AccessCodes} />
                            <Route path="policies/policy_testing" component={PolicyTesting} />

                            {canAccessBrowserIso ? <Fragment>
                                <Route path="product_settings/general_options" component={GeneralOptions} />
                            </Fragment> : null}

                            <Route path="policies" component={BrowserPolicies} />

                            {canAccessUrlIso ? <Fragment>
                                <Route path="url_isolation_policies" component={ConsoleUDPolicies} />
                            </Fragment> : null}

                            {canAccessBrowserIso ? <Fragment>
                                <Route path="url_isolation_policies" component={ConsoleUDPolicies} />
                                <Route path="registration_options" component={RegistrationOptions} />
                            </Fragment> : null}

                            <Redirect from="*" to={getDefaultRoute()} />
                        </Route>
                    </Route>
                </Router>
            </RootPage>
        </GAController>
    </Provider>
</I18nextProvider>, document.getElementById('root'));

window.STATE = stores;
